/**
 * CFO Financial Metrics Service
 *
 * Calculates executive-level financial metrics beyond operational ROI.
 * Translates energy savings into strategic business value:
 * - EBITDA impact and margin improvement
 * - Revenue growth equivalence
 * - Market capitalization / enterprise value impact
 * - Capital efficiency (NPV, IRR, ROIC)
 * - ESG / sustainability value
 * - REIT-specific metrics (NOI, FFO/AFFO, property value)
 *
 * KEY INSIGHT:
 * CFOs don't think in "15-month payback" - they think in:
 * "What revenue growth delivers the same profit?"
 * "How does this affect shareholder value?"
 * "What's the IRR vs. our hurdle rate?"
 *
 * INDUSTRY STANDARD DEFAULTS:
 * - EBITDA multiple: 8x (typical across industries)
 * - Net profit margin: 5%
 * - WACC: 10%
 * - Tax rate: 25%
 * - Equipment life: 15 years
 */

// ============================================================================
// INDUSTRY STANDARD DEFAULTS
// ============================================================================

/**
 * Industry standard financial assumptions
 * Used when company doesn't provide custom inputs
 */
export const INDUSTRY_DEFAULTS = {
  ebitdaMultiple: 8,        // 8x EBITDA (typical for most industries)
  profitMargin: 0.05,       // 5% net profit margin (industry typical)
  wacc: 0.10,               // 10% weighted average cost of capital
  taxRate: 0.25,            // 25% effective tax rate
  equipmentLifeYears: 15,   // Average HVAC equipment operational life
  capRate: 0.08,            // 8% capitalization rate (real estate)
  depreciationYears: 15     // Straight-line depreciation period
};

// ============================================================================
// TIER 1: EBITDA IMPACT
// ============================================================================

/**
 * Calculate EBITDA impact from energy savings
 * Energy savings = direct EBITDA contribution (dollar-for-dollar)
 *
 * @param {number} annualSavings - Annual energy cost savings
 * @param {number} currentRevenue - Company's annual revenue (optional)
 * @returns {Object} EBITDA impact metrics
 */
export function calculateEBITDAImpact(annualSavings, currentRevenue = null) {
  // Energy savings contribute directly to EBITDA
  const directEBITDAContribution = annualSavings;

  // Calculate margin improvement (if revenue known)
  let ebitdaMarginImprovement = null;
  if (currentRevenue && currentRevenue > 0) {
    ebitdaMarginImprovement = (annualSavings / currentRevenue) * 100;
  }

  return {
    contribution: directEBITDAContribution,
    marginImprovement: ebitdaMarginImprovement,
    description: 'Energy savings flow directly to EBITDA (no revenue required)'
  };
}

// ============================================================================
// TIER 2: REVENUE EQUIVALENCE
// ============================================================================

/**
 * Calculate revenue growth equivalent to energy savings
 * "What revenue growth delivers the same bottom-line impact?"
 *
 * Example: $400K savings ÷ 5% margin = $8M equivalent revenue
 *
 * @param {number} annualSavings - Annual energy cost savings
 * @param {number} profitMargin - Net profit margin (decimal, e.g., 0.05 = 5%)
 * @param {number} currentRevenue - Current annual revenue (optional)
 * @returns {Object} Revenue equivalence metrics
 */
export function calculateRevenueEquivalence(annualSavings, profitMargin, currentRevenue = null) {
  // Calculate equivalent revenue needed to generate same profit
  // Guard against division by zero
  const equivalentRevenue = profitMargin > 0
    ? annualSavings / profitMargin
    : 0;

  // Calculate as percentage of current revenue (if known)
  let revenueGrowthPercent = null;
  if (currentRevenue && currentRevenue > 0) {
    revenueGrowthPercent = (equivalentRevenue / currentRevenue) * 100;
  }

  return {
    equivalentRevenue,
    revenueGrowthPercent,
    profitMarginUsed: profitMargin,
    description: `Equivalent to ${formatCurrency(equivalentRevenue)} in new revenue at ${(profitMargin * 100).toFixed(1)}% margin`
  };
}

// ============================================================================
// TIER 3: MARKET VALUATION IMPACT
// ============================================================================

/**
 * Calculate market capitalization impact (public companies)
 * Uses EV/EBITDA multiple to estimate shareholder value creation
 *
 * @param {number} annualSavings - Annual energy cost savings
 * @param {number} ebitdaMultiple - Company's EBITDA multiple (default: 8x)
 * @returns {Object} Market cap impact metrics
 */
export function calculateMarketCapImpact(annualSavings, ebitdaMultiple = INDUSTRY_DEFAULTS.ebitdaMultiple) {
  // Estimated market cap increase
  const marketCapIncrease = annualSavings * ebitdaMultiple;

  // Enterprise value increase (same calculation, different context)
  const enterpriseValueIncrease = marketCapIncrease;

  return {
    marketCapIncrease,
    enterpriseValueIncrease,
    multipleUsed: ebitdaMultiple,
    description: `Estimated ${formatCurrency(marketCapIncrease)} shareholder value creation at ${ebitdaMultiple}x EBITDA`
  };
}

/**
 * Calculate enterprise value impact (private companies)
 * Same formula as market cap, different framing
 *
 * @param {number} annualSavings - Annual energy cost savings
 * @param {number} ebitdaMultiple - Company's EBITDA multiple (default: 8x)
 * @returns {Object} Enterprise value metrics
 */
export function calculateEnterpriseValue(annualSavings, ebitdaMultiple = INDUSTRY_DEFAULTS.ebitdaMultiple) {
  const valueIncrease = annualSavings * ebitdaMultiple;

  return {
    valueIncrease,
    multipleUsed: ebitdaMultiple,
    description: `Estimated ${formatCurrency(valueIncrease)} enterprise value creation`
  };
}

// ============================================================================
// TIER 4: CAPITAL EFFICIENCY METRICS
// ============================================================================

/**
 * Calculate Net Present Value (NPV)
 * Discounts future cash flows to present value
 *
 * @param {number} treatmentCost - Initial investment
 * @param {number} annualSavings - Annual cash flow
 * @param {number} wacc - Weighted average cost of capital (decimal)
 * @param {number} years - Investment horizon
 * @returns {number} Net present value
 */
export function calculateNPV(treatmentCost, annualSavings, wacc = INDUSTRY_DEFAULTS.wacc, years = INDUSTRY_DEFAULTS.equipmentLifeYears) {
  let npv = -treatmentCost;  // Initial outlay

  for (let year = 1; year <= years; year++) {
    const discountFactor = Math.pow(1 + wacc, year);
    npv += annualSavings / discountFactor;
  }

  return npv;
}

/**
 * Calculate Internal Rate of Return (IRR)
 * Uses geometric mean approximation for uniform cash flows
 *
 * NOTE: This is an approximation that may underestimate IRR by ~3-4% for
 * very short payback periods. For precise IRR calculations, implement
 * Newton-Raphson iteration or use a financial library like finance.js.
 *
 * @param {number} treatmentCost - Initial investment
 * @param {number} annualSavings - Annual cash flow
 * @param {number} years - Investment horizon
 * @returns {number} IRR as decimal (e.g., 0.75 = 75%)
 */
export function calculateIRR(treatmentCost, annualSavings, years = INDUSTRY_DEFAULTS.equipmentLifeYears) {
  // Geometric mean approximation for uniform cash flows
  // For more accurate results, implement Newton-Raphson iteration

  // For uniform cash flows, IRR where NPV = 0
  // Approximation: IRR ≈ (total returns / investment) / years
  const totalReturns = annualSavings * years;
  const simpleReturn = (totalReturns - treatmentCost) / treatmentCost;

  // Rough IRR approximation
  // For very short payback periods, IRR is very high
  if (treatmentCost <= 0) return 0;

  const paybackYears = treatmentCost / annualSavings;

  // Simplified IRR formula for uniform cash flows
  if (paybackYears >= years) {
    return simpleReturn / years;
  }

  // For short payback, use geometric approximation
  const irr = Math.pow(1 + simpleReturn, 1 / years) - 1;

  return Math.min(irr, 5.0);  // Cap at 500% for sanity
}

/**
 * Calculate Return on Invested Capital (ROIC)
 * Annual return after tax divided by invested capital
 *
 * @param {number} treatmentCost - Initial investment
 * @param {number} annualSavings - Annual cash flow
 * @param {number} taxRate - Effective tax rate (decimal)
 * @returns {number} ROIC as decimal (e.g., 0.60 = 60%)
 */
export function calculateROIC(treatmentCost, annualSavings, taxRate = INDUSTRY_DEFAULTS.taxRate) {
  if (treatmentCost <= 0) return 0;

  const afterTaxSavings = annualSavings * (1 - taxRate);
  const roic = afterTaxSavings / treatmentCost;

  return roic;
}

/**
 * Calculate complete capital efficiency metrics
 *
 * @param {number} treatmentCost - Initial investment
 * @param {number} annualSavings - Annual cash flow
 * @param {Object} config - Financial configuration
 * @returns {Object} Complete capital efficiency metrics
 */
export function calculateCapitalEfficiency(treatmentCost, annualSavings, config = {}) {
  const wacc = config.wacc || INDUSTRY_DEFAULTS.wacc;
  const taxRate = config.taxRate || INDUSTRY_DEFAULTS.taxRate;
  const years = config.equipmentLifeYears || INDUSTRY_DEFAULTS.equipmentLifeYears;

  const npv = calculateNPV(treatmentCost, annualSavings, wacc, years);
  const irr = calculateIRR(treatmentCost, annualSavings, years);
  const roic = calculateROIC(treatmentCost, annualSavings, taxRate);

  // Simple payback period
  const paybackYears = treatmentCost / annualSavings;
  const paybackMonths = paybackYears * 12;

  return {
    npv,
    irr,
    roic,
    paybackMonths,
    paybackYears,
    waccUsed: wacc,
    description: `NPV: ${formatCurrency(npv)}, IRR: ${(irr * 100).toFixed(0)}%, ROIC: ${(roic * 100).toFixed(0)}%`
  };
}

// ============================================================================
// TIER 5: ESG / SUSTAINABILITY VALUE
// ============================================================================

/**
 * Calculate environmental / ESG impact metrics
 *
 * @param {number} kwhSaved - Annual kWh saved
 * @returns {Object} ESG impact metrics
 */
export function calculateESGImpact(kwhSaved) {
  // EPA conversion factor: 0.42 kg CO2 per kWh
  const co2AvoidedKg = kwhSaved * 0.42;
  const co2AvoidedTonnes = co2AvoidedKg / 1000;

  // Vehicle equivalent (average passenger vehicle: 4.6 tonnes CO2/year)
  const vehicleEquivalent = co2AvoidedTonnes / 4.6;

  // Trees equivalent (1 tree absorbs ~21 kg CO2/year)
  const treesEquivalent = co2AvoidedKg / 21;

  return {
    co2AvoidedTonnes,
    co2AvoidedKg,
    vehicleEquivalent: Math.round(vehicleEquivalent),
    treesEquivalent: Math.round(treesEquivalent),
    kwhSaved,
    description: `${formatNumber(co2AvoidedTonnes, 1)} tonnes CO₂ avoided = removing ${Math.round(vehicleEquivalent)} cars from the road`
  };
}

// ============================================================================
// TIER 6: REIT-SPECIFIC METRICS
// ============================================================================

/**
 * Calculate REIT-specific financial metrics
 * Focus on NOI, property value, cap rate, FFO/AFFO
 *
 * @param {number} annualSavings - Annual energy cost savings
 * @param {Object} config - REIT configuration
 * @returns {Object} REIT metrics
 */
export function calculateREITMetrics(annualSavings, config = {}) {
  const propertyValue = config.propertyValue || null;
  const currentCapRate = config.capRate || INDUSTRY_DEFAULTS.capRate;
  const currentNOI = config.currentNOI || (propertyValue ? propertyValue * currentCapRate : null);
  const squareFootage = config.squareFootage || null;
  const totalOpEx = config.totalOpEx || null;
  const annualDebtService = config.annualDebtService || null;

  // NOI improvement (energy savings = direct NOI increase)
  const noiImprovement = annualSavings;

  // Property value increase (using cap rate)
  // Property Value = NOI / Cap Rate
  // Guard against division by zero
  const propertyValueIncrease = currentCapRate > 0
    ? noiImprovement / currentCapRate
    : 0;

  // New NOI and property value (if baseline provided)
  let newNOI = null;
  let newPropertyValue = null;
  let newCapRate = null;

  if (currentNOI && propertyValue) {
    newNOI = currentNOI + noiImprovement;
    newPropertyValue = propertyValue + propertyValueIncrease;
    newCapRate = newNOI / newPropertyValue;
  }

  // FFO/AFFO impact (simplified - energy savings increase FFO directly)
  const ffoImpact = annualSavings;
  const affoImpact = annualSavings;  // Simplified (AFFO = FFO - capex)

  // =====================
  // NEW EXTENDED METRICS
  // =====================

  // 1. NOI per Square Foot Improvement
  let noiPerSFImprovement = null;
  if (squareFootage && squareFootage > 0) {
    noiPerSFImprovement = annualSavings / squareFootage;
  }

  // 2. Operating Expense Ratio Reduction
  // Shows what % of OpEx is reduced by energy savings
  let opExRatioReduction = null;
  if (totalOpEx && totalOpEx > 0) {
    opExRatioReduction = (annualSavings / totalOpEx) * 100;
  }

  // 3. DSCR (Debt Service Coverage Ratio) Impact
  // Shows additional coverage from savings
  let dscrImpact = null;
  if (annualDebtService && annualDebtService > 0) {
    dscrImpact = annualSavings / annualDebtService;
  }

  // 4. Cap Rate Yield Improvement (basis points)
  // Shows effective yield increase on property value
  let capRateYieldImprovement = null;
  if (propertyValue && propertyValue > 0) {
    capRateYieldImprovement = (annualSavings / propertyValue) * 100;
  }

  return {
    // Core metrics
    noiImprovement,
    propertyValueIncrease,
    currentNOI,
    newNOI,
    currentPropertyValue: propertyValue,
    newPropertyValue,
    currentCapRate,
    newCapRate,
    ffoImpact,
    affoImpact,

    // Extended metrics
    noiPerSFImprovement,
    opExRatioReduction,
    dscrImpact,
    capRateYieldImprovement,

    // Input values (for display)
    squareFootage,
    totalOpEx,
    annualDebtService,

    description: `NOI +${formatCurrency(noiImprovement)}/yr → Property value +${formatCurrency(propertyValueIncrease)} @ ${(currentCapRate * 100).toFixed(1)}% cap rate`
  };
}

// ============================================================================
// TIER 7: PUBLIC COMPANY-SPECIFIC METRICS
// ============================================================================

/**
 * Calculate Public Company-specific financial metrics
 * Focus on EPS, operating margin, FCF, ESG/carbon, and P/E sensitivity
 *
 * @param {number} annualSavings - Annual energy cost savings
 * @param {number} kWhSavings - Annual kWh saved
 * @param {Object} config - Public company configuration
 * @returns {Object} Public company metrics
 */
export function calculatePublicCompanyMetrics(annualSavings, kWhSavings, config = {}) {
  const sharesOutstanding = config.sharesOutstanding || null;
  const totalOpEx = config.totalOpEx || null;
  const peRatio = config.peRatio || 20;
  const carbonPrice = config.carbonPrice || 50;

  // 1. EPS Impact (earnings per share improvement)
  let epsImpact = null;
  if (sharesOutstanding && sharesOutstanding > 0) {
    epsImpact = annualSavings / sharesOutstanding;
  }

  // 2. Operating Margin Improvement
  let opMarginImprovement = null;
  if (totalOpEx && totalOpEx > 0) {
    opMarginImprovement = (annualSavings / totalOpEx) * 100;
  }

  // 3. Free Cash Flow Impact (energy savings = direct FCF)
  const fcfImpact = annualSavings;

  // 4. Scope 2 Emissions Reduction
  // EPA eGRID average: 0.417 kg CO2/kWh = 0.000417 metric tons/kWh
  const gridEmissionsFactor = 0.000417;
  const scope2Reduction = kWhSavings * gridEmissionsFactor;

  // 5. Carbon Cost Avoidance (future carbon pricing risk mitigation)
  const carbonCostAvoidance = scope2Reduction * carbonPrice;

  // 6. P/E Multiple Sensitivity (how EPS improvement affects stock value)
  let peValueImpact = null;
  if (epsImpact && peRatio > 0 && sharesOutstanding) {
    peValueImpact = epsImpact * peRatio * sharesOutstanding;
  }

  return {
    // Core metrics
    epsImpact,
    opMarginImprovement,
    fcfImpact,
    scope2Reduction,
    carbonCostAvoidance,
    peValueImpact,

    // Input values (for display)
    sharesOutstanding,
    totalOpEx,
    peRatio,
    carbonPrice,
    kWhSavings,

    description: `EPS +$${epsImpact ? epsImpact.toFixed(4) : 'N/A'}/share, ${scope2Reduction.toFixed(1)} tonnes CO₂ avoided`
  };
}

// ============================================================================
// COMPREHENSIVE CFO METRICS CALCULATION
// ============================================================================

/**
 * Calculate all CFO metrics at once
 *
 * @param {Object} roiResults - Results from calculateEASROI()
 * @param {Object} companyConfig - Company financial configuration
 * @returns {Object} Complete CFO metrics across all 6 tiers
 */
export function calculateAllCFOMetrics(roiResults, companyConfig = {}) {
  // Validate input data
  if (!roiResults || !roiResults.portfolio) {
    console.error('calculateAllCFOMetrics: Missing roiResults.portfolio', roiResults);
    throw new Error('Invalid roiResults: portfolio data is required');
  }

  const {
    portfolio
  } = roiResults;

  // Validate portfolio structure
  if (!portfolio.summary) {
    console.error('calculateAllCFOMetrics: Missing portfolio.summary', portfolio);
    throw new Error('Invalid portfolio data: summary is required');
  }

  const {
    total_annual_savings,
    total_treatment_cost,
    total_kwh_saved
  } = portfolio.summary;

  // Validate required values
  if (typeof total_annual_savings !== 'number' || typeof total_treatment_cost !== 'number') {
    console.error('calculateAllCFOMetrics: Invalid values', { total_annual_savings, total_treatment_cost });
    throw new Error('Invalid portfolio data: savings and treatment cost must be numbers');
  }

  // Get configuration with defaults
  const config = {
    companyType: companyConfig.companyType || 'private',
    annualRevenue: companyConfig.annualRevenue || null,
    profitMargin: companyConfig.profitMargin || INDUSTRY_DEFAULTS.profitMargin,
    ebitdaMultiple: companyConfig.ebitdaMultiple || INDUSTRY_DEFAULTS.ebitdaMultiple,
    wacc: companyConfig.wacc || INDUSTRY_DEFAULTS.wacc,
    taxRate: companyConfig.taxRate || INDUSTRY_DEFAULTS.taxRate,
    equipmentLifeYears: companyConfig.equipmentLifeYears || INDUSTRY_DEFAULTS.equipmentLifeYears,

    // REIT-specific
    propertyValue: companyConfig.propertyValue || null,
    capRate: companyConfig.capRate || INDUSTRY_DEFAULTS.capRate,
    currentNOI: companyConfig.currentNOI || null,
    squareFootage: companyConfig.squareFootage || null,
    totalOpEx: companyConfig.totalOpEx || null,
    annualDebtService: companyConfig.annualDebtService || null,

    // Public Company-specific
    sharesOutstanding: companyConfig.sharesOutstanding || null,
    peRatio: companyConfig.peRatio || 20,
    carbonPrice: companyConfig.carbonPrice || 50
  };

  // Tier 1: EBITDA Impact
  const ebitdaMetrics = calculateEBITDAImpact(total_annual_savings, config.annualRevenue);

  // Tier 2: Revenue Equivalence
  const revenueMetrics = calculateRevenueEquivalence(
    total_annual_savings,
    config.profitMargin,
    config.annualRevenue
  );

  // Tier 3: Market Valuation (conditional on company type)
  let valuationMetrics;
  if (config.companyType === 'public') {
    valuationMetrics = calculateMarketCapImpact(total_annual_savings, config.ebitdaMultiple);
  } else {
    valuationMetrics = calculateEnterpriseValue(total_annual_savings, config.ebitdaMultiple);
  }

  // Tier 4: Capital Efficiency
  const capitalMetrics = calculateCapitalEfficiency(total_treatment_cost, total_annual_savings, config);

  // Tier 5: ESG Impact
  const esgMetrics = calculateESGImpact(total_kwh_saved);

  // Tier 6: REIT Metrics (only if company type is REIT)
  let reitMetrics = null;
  if (config.companyType === 'reit') {
    reitMetrics = calculateREITMetrics(total_annual_savings, config);
  }

  // Tier 7: Public Company Metrics (only if company type is public)
  let publicCompanyMetrics = null;
  if (config.companyType === 'public') {
    publicCompanyMetrics = calculatePublicCompanyMetrics(total_annual_savings, total_kwh_saved, config);
  }

  return {
    tier1_ebitda: ebitdaMetrics,
    tier2_revenue: revenueMetrics,
    tier3_valuation: valuationMetrics,
    tier4_capital: capitalMetrics,
    tier5_esg: esgMetrics,
    tier6_reit: reitMetrics,
    tier7_public: publicCompanyMetrics,

    // Configuration used
    configuration: config,

    // Metadata
    calculated_at: new Date().toISOString()
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format currency value
 *
 * @param {number} value - Dollar amount
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

/**
 * Format number with commas
 *
 * @param {number} value - Number to format
 * @param {number} decimals - Decimal places
 * @returns {string} Formatted number string
 */
function formatNumber(value, decimals = 0) {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value);
}

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Tier functions
  calculateEBITDAImpact,
  calculateRevenueEquivalence,
  calculateMarketCapImpact,
  calculateEnterpriseValue,
  calculateCapitalEfficiency,
  calculateESGImpact,
  calculateREITMetrics,
  calculatePublicCompanyMetrics,

  // Capital efficiency components
  calculateNPV,
  calculateIRR,
  calculateROIC,

  // Main function
  calculateAllCFOMetrics,

  // Constants
  INDUSTRY_DEFAULTS
};
